// Reusable "chart insights" card — every projection chart mounts one of these
// so users never have to interpret raw numbers themselves.
//
// Consumes the `insights` block returned by the backend projection engine:
//   { summary, key_insights[], opportunities[], risks[], actions[] }
import { Sparkles, TrendingUp, AlertTriangle, ArrowRight, Compass } from "lucide-react";

export default function ChartInsightsCard({ insights, onAction, className = "" }) {
  if (!insights) return null;
  return (
    <div
      className={`rounded-xl border border-white/10 bg-card/40 backdrop-blur-sm ${className}`}
      data-testid="chart-insights"
    >
      {/* summary */}
      {insights.summary && (
        <div className="p-4 sm:p-5 border-b border-white/5 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/25 to-purple-500/25 border border-white/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
              AI Summary
            </div>
            <div className="text-sm leading-relaxed">{insights.summary}</div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
        {/* Key insights */}
        {insights.key_insights?.length > 0 && (
          <List
            icon={<Compass className="w-4 h-4 text-blue-400" />}
            title="Key insights"
            items={insights.key_insights}
          />
        )}
        {/* Opportunities */}
        {insights.opportunities?.length > 0 && (
          <List
            icon={<TrendingUp className="w-4 h-4 text-emerald-400" />}
            title="Biggest opportunities"
            items={insights.opportunities}
          />
        )}
      </div>

      {insights.risks?.length > 0 && (
        <div className="border-t border-white/5">
          <List
            icon={<AlertTriangle className="w-4 h-4 text-amber-400" />}
            title="Biggest risks"
            items={insights.risks}
          />
        </div>
      )}

      {insights.actions?.length > 0 && (
        <div className="border-t border-white/5 p-4 sm:p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
            Recommended actions
          </div>
          <div className="flex flex-wrap gap-2">
            {insights.actions.map((a, i) => (
              <button
                key={i}
                onClick={() => onAction && onAction(a)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-colors"
                data-testid={`insight-action-${a.target || i}`}
              >
                {a.label}
                <ArrowRight className="w-3 h-3 opacity-60" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function List({ icon, title, items }) {
  return (
    <div className="p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {title}
        </div>
      </div>
      <ul className="space-y-2 list-none pl-0">
        {items.map((it, i) => (
          <li key={i} className="text-xs leading-relaxed text-muted-foreground flex gap-2">
            <span aria-hidden className="text-primary/70">▸</span>
            <span className="flex-1">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
